import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, isBefore, isAfter, isToday, parseISO } from 'date-fns';

export interface Promise {
  id: string;
  payment_id: string;
  contact_id: string;
  contact_date: string;
  promised_date: string;
  promised_amount: number | null;
  notes: string | null;
  outcome: string;
  // Joined fields
  client_name: string;
  payment_amount: number;
  payment_currency: string;
  payment_status: string;
  days_overdue: number;
}

export type PromiseStatus = 'pending' | 'fulfilled' | 'broken';

export interface PromiseWithStatus extends Promise {
  promise_status: PromiseStatus;
  days_since_promise: number;
}

function calculatePromiseStatus(promise: Promise): PromiseStatus {
  // If payment is paid, promise is fulfilled
  if (promise.payment_status === 'paid') {
    return 'fulfilled';
  }
  
  const promiseDate = parseISO(promise.promised_date);
  const today = startOfDay(new Date());
  
  // If promise date is in the past and not paid, it's broken
  if (isBefore(promiseDate, today)) {
    return 'broken';
  }
  
  // Otherwise it's pending
  return 'pending';
}

function getDaysSincePromise(promisedDate: string): number {
  const promise = parseISO(promisedDate);
  const today = startOfDay(new Date());
  const diffTime = today.getTime() - promise.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

export function usePromisesDashboard() {
  return useQuery({
    queryKey: ['promises', 'dashboard'],
    queryFn: async () => {
      // Get all promises (collection_history entries with outcome = 'promised')
      const { data, error } = await supabase
        .from('collection_history')
        .select(`
          id,
          payment_id,
          contact_id,
          contact_date,
          promised_date,
          promised_amount,
          notes,
          outcome
        `)
        .eq('outcome', 'promised')
        .not('promised_date', 'is', null)
        .order('promised_date', { ascending: true });

      if (error) throw error;

      // Get payment and contact details for each promise
      const paymentIds = [...new Set(data.map(p => p.payment_id))];
      const contactIds = [...new Set(data.map(p => p.contact_id))];

      const [paymentsResult, contactsResult] = await Promise.all([
        supabase
          .from('payments')
          .select('id, amount, status')
          .in('id', paymentIds),
        supabase
          .from('contacts')
          .select('id, first_name, last_name')
          .in('id', contactIds)
      ]);

      if (paymentsResult.error) throw paymentsResult.error;
      if (contactsResult.error) throw contactsResult.error;

      const paymentsMap = new Map(paymentsResult.data.map(p => [p.id, p]));
      const contactsMap = new Map(contactsResult.data.map(c => [c.id, c]));

      // Combine data
      const promises: PromiseWithStatus[] = data.map(entry => {
        const payment = paymentsMap.get(entry.payment_id);
        const contact = contactsMap.get(entry.contact_id);
        
        const promise: Promise = {
          ...entry,
          promised_date: entry.promised_date!,
          client_name: contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : 'Cliente',
          payment_amount: payment?.amount || 0,
          payment_currency: 'USD',
          payment_status: payment?.status || 'pending',
          days_overdue: 0,
        };

        return {
          ...promise,
          promise_status: calculatePromiseStatus(promise),
          days_since_promise: getDaysSincePromise(promise.promised_date),
        };
      });

      // Categorize promises
      const today = startOfDay(new Date());
      
      const promisesToday = promises.filter(p => 
        isToday(parseISO(p.promised_date)) && p.promise_status === 'pending'
      );
      
      const promisesOverdue = promises.filter(p => 
        p.promise_status === 'broken'
      ).sort((a, b) => b.days_since_promise - a.days_since_promise);
      
      const promisesFuture = promises.filter(p => 
        isAfter(parseISO(p.promised_date), today) && p.promise_status === 'pending'
      );
      
      const promisesFulfilled = promises.filter(p => 
        p.promise_status === 'fulfilled'
      );

      return {
        all: promises,
        today: promisesToday,
        overdue: promisesOverdue,
        future: promisesFuture,
        fulfilled: promisesFulfilled,
        summary: {
          totalToday: promisesToday.length,
          totalOverdue: promisesOverdue.length,
          totalFuture: promisesFuture.length,
          totalFulfilled: promisesFulfilled.length,
          amountToday: promisesToday.reduce((sum, p) => sum + (p.promised_amount || p.payment_amount), 0),
          amountOverdue: promisesOverdue.reduce((sum, p) => sum + (p.promised_amount || p.payment_amount), 0),
        }
      };
    },
  });
}
